import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import moment from "moment";
import {
  LuBuilding2,
  LuDatabase,
  LuFilter,
  LuPencil,
  LuPlus,
  LuSearch,
  LuTrash2,
} from "react-icons/lu";
import { toast } from "react-toastify";

import AccountTypeFormModal from "./AccountTypeFormModal";
import Pagination from "../pagination";
import Button from "../ui/Button";
import Input from "../ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import {
  EmptyState,
  LoadingState,
  TableEmptyRow,
  TableLoadingRow,
} from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  actionBarClass,
  buttonSpinnerClass,
  cardClass,
  cardContentClass,
  cardContentFlushClass,
  cardHeaderClass,
  cardTitleClass,
  creditCellClass,
  debitCellClass,
  fieldIconClass,
  fieldInputClass,
  filterFooterClass,
  filterGridClass,
  filterLabelClass,
  mobileCardClass,
  segmentActiveClass,
  segmentButtonClass,
  segmentInactiveClass,
  segmentTrackClass,
  tableHeadCellClass,
} from "../ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchAccountTypesListData,
  getAccountTypes,
  getEditAccountType,
  getTotalAccountTypes,
  setEditAccountType,
  toggleAccountTypeModal,
} from "../../features/accounts/accountTypesSlice";
import {
  getIsShowAccounts,
  setIsShowAccounts,
} from "../../features/accounts/accountsSlice";
import {
  getListState,
  saveListState,
} from "../../features/listState/listStateSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import useConfirmPopup from "../../context/useConfirmPopup";
import { DELETE, POST, PUT } from "../../services/axios.service";
import { hasPermission, permission } from "../../static/permission";
import { toastStyles } from "../../static/styles";
import AccessDenied from "../ui/AccessDenied";
import { AccountTypesProps, IFormInput } from "../../types/accountTypes";

const formatAmount = (value: number | null) =>
  (value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value: string) =>
  moment(value).format("DD-MM-YYYY hh:mm A");

const actionSpinnerClass = cx(
  buttonSpinnerClass,
  "mr-0 border-[#113F67]/30 border-t-[#113F67]"
);

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const listKey = "accounts:types";

const AccountTypeList = () => {
  const dispatch = useAppDispatch();
  const confirmDelete = useConfirmPopup();

  const loggingUser = useAppSelector(getLoggingUser);
  const savedList = useAppSelector((state) => getListState(state, listKey));

  const canRead = hasPermission(
    loggingUser?.permissions,
    permission.accountTypesRead
  );
  const canCreate = hasPermission(
    loggingUser?.permissions,
    permission.accountTypesCreate
  );
  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.accountTypesUpdate
  );
  const canDelete = hasPermission(
    loggingUser?.permissions,
    permission.accountTypesDelete
  );

  const actionCount = (canUpdate ? 1 : 0) + (canDelete ? 1 : 0);
  const tableColSpan = 7 + (actionCount > 0 ? 1 : 0);

  const isShowAccounts = useAppSelector(getIsShowAccounts);
  const accountTypes = useAppSelector(getAccountTypes);
  const totalAccountTypes = useAppSelector(getTotalAccountTypes);
  const editAccountType = useAppSelector(getEditAccountType);

  interface FilterFormInput {
    name: string;
    fromDate: string;
    toDate: string;
  }

  const emptyFilters: FilterFormInput = {
    name: "",
    fromDate: "",
    toDate: "",
  };

  const { handleSubmit, register, reset, getValues } =
    useForm<FilterFormInput>({ defaultValues: savedList.filters });

  const [page, setPage] = useState<number>(savedList.page || 1);
  const [pageSize, setPageSize] = useState<number>(savedList.pageSize || 50);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState<boolean>(
    !!savedList.showFilter
  );
  const [isActionLoading, setIsActionLoading] = useState<number>(0);

  const fetchId = useRef(0);

  const paginate = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const handleShowAccountTypeForm = async () => {
    await dispatch(setEditAccountType(null));
    dispatch(toggleAccountTypeModal(true));
  };

  const handleEditAccountType = async (accountType: AccountTypesProps) => {
    await dispatch(setEditAccountType(accountType));
    dispatch(toggleAccountTypeModal(true));
  };

  const handleSaveAccountType = async (data: IFormInput) => {
    try {
      if (editAccountType && editAccountType.id) {
        await PUT(`/v1/admin/account-types/${editAccountType.id}`, {
          name: data.name,
          phone: data.phone,
          description: data.description,
        })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              await dispatch(setEditAccountType(null));

              if (page === 1) {
                fetchAccountTypesList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      } else {
        await POST("/v1/admin/account-types", {
          name: data.name,
          phone: data.phone,
          description: data.description,
        })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              if (page === 1) {
                fetchAccountTypesList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      }
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  const handleDeleteAccountType = async (id: number) => {
    try {
      await DELETE(`/v1/admin/account-types/${id}`)
        .then(async (response: any) => {
          if (response.data.status === "success") {
            const pageNumber = accountTypes.length > 1 ? page : page - 1;
            if (page === pageNumber || pageNumber < 1) {
              fetchAccountTypesList(page, pageSize);
            } else {
              setPage(pageNumber);
            }

            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  const handleDeleteRow = async (id: number) => {
    const confirmed = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (confirmed) {
      setIsActionLoading(id);
      await handleDeleteAccountType(id);
      setIsActionLoading(0);
    }
  };

  const handleOnFilterSubmit = async () => {
    if (page === 1) {
      fetchAccountTypesList(page, pageSize);
    } else {
      setPage(1);
    }
  };

  const handleClearFilter = () => {
    reset(emptyFilters);
    if (page === 1) {
      fetchAccountTypesList(page, pageSize);
    } else {
      setPage(1);
    }
  };

  const fetchAccountTypesList = async (page: number, size: number) => {
    const data = getValues();
    const currentFetch = ++fetchId.current;

    dispatch(saveListState({ key: listKey, value: { filters: data } }));
    setIsLoading(true);

    dispatch(
      fetchAccountTypesListData({
        page: page,
        size: size,
        search: data?.name || null,
        fromDate: data?.fromDate || null,
        toDate: data?.toDate || null,
      })
    ).then((action: any) => {
      const result = action?.payload?.data;

      if (currentFetch === fetchId.current && result?.status !== "success") {
        toast.error(
          result?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      }

      // A saved page that is now past the end (its rows deleted, say) moves to the last page.
      const lastPage = Math.max(
        1,
        Math.ceil((Number(result?.data?.total_records) || 0) / size)
      );

      if (
        currentFetch === fetchId.current &&
        page > 1 &&
        result?.status === "success" &&
        !result?.data?.records?.length &&
        lastPage < page
      ) {
        setPage(lastPage);
        return;
      }

      setIsLoading(false);
    });
  };

  // Coming back to the list (from a record's page, say) shows the same page and filters.
  useEffect(() => {
    dispatch(
      saveListState({ key: listKey, value: { page, pageSize, showFilter } })
    );
  }, [dispatch, page, pageSize, showFilter]);

  useEffect(() => {
    fetchAccountTypesList(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  if (!canRead) {
    return <AccessDenied message="તમને આ વિગતો જોવાની પરવાનગી નથી." />;
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className={actionBarClass}>
        <div className="flex items-center gap-3">
          <div className={segmentTrackClass}>
            <button
              type="button"
              onClick={() => isShowAccounts || dispatch(setIsShowAccounts(true))}
              className={cx(
                segmentButtonClass,
                isShowAccounts ? segmentActiveClass : segmentInactiveClass
              )}
            >
              રોજમેળ
            </button>

            <button
              type="button"
              onClick={() =>
                !isShowAccounts || dispatch(setIsShowAccounts(false))
              }
              className={cx(
                segmentButtonClass,
                !isShowAccounts ? segmentActiveClass : segmentInactiveClass
              )}
            >
              પાર્ટીઓ
            </button>
          </div>
        </div>

        <div
          className={cx(
            "grid w-full gap-2 sm:flex sm:w-auto",
            canCreate ? "grid-cols-2" : "grid-cols-1"
          )}
        >
          <Button variant="outline" onClick={() => setShowFilter(!showFilter)}>
            <LuFilter className="h-4 w-4" />
            ફિલ્ટર
          </Button>

          {canCreate && (
            <Button variant="primary" onClick={handleShowAccountTypeForm}>
              <LuPlus className="h-4 w-4" />
              નવી પાર્ટી ઉમેરો
            </Button>
          )}
        </div>
      </div>

      {showFilter && (
        <div className={cardClass}>
          <div className={cardHeaderClass}>
            <div className={`${cardTitleClass} text-lg`}>ફિલ્ટર</div>
          </div>

          <div className={cardContentClass}>
            <form onSubmit={handleSubmit(handleOnFilterSubmit)}>
              <div className={filterGridClass}>
                <div className="space-y-2">
                  <label className={filterLabelClass}>તારીખ થી</label>
                  <div className="relative">
                    <LuDatabase className={fieldIconClass} />
                    <Input
                      type="date"
                      className={fieldInputClass()}
                      {...register("fromDate")}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className={filterLabelClass}>તારીખ સુધી</label>
                  <div className="relative">
                    <LuDatabase className={fieldIconClass} />
                    <Input
                      type="date"
                      className={fieldInputClass()}
                      {...register("toDate")}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className={filterLabelClass}>શોધો</label>
                  <div className="relative">
                    <LuSearch className={fieldIconClass} />
                    <Input
                      placeholder="ID, નામ અથવા ફોન નંબર શોધો..."
                      className={fieldInputClass()}
                      {...register("name")}
                    />
                  </div>
                </div>
              </div>

              <div className={filterFooterClass}>
                <Button
                  type="button"
                  variant="outline"
                  className="max-sm:flex-1"
                  onClick={handleClearFilter}
                >
                  ફિલ્ટર સાફ કરો
                </Button>

                <Button
                  type="submit"
                  variant="primary"
                  className="max-sm:flex-1"
                >
                  ફિલ્ટર લાગુ કરો
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className={cx(cardClass, "max-sm:hidden")}>
        <div className={cardContentFlushClass}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={tableHeadCellClass}>ક્રમ</TableHead>
                  <TableHead className={tableHeadCellClass}>નામ</TableHead>
                  <TableHead className={tableHeadCellClass}>ફોન નંબર</TableHead>
                  <TableHead className={tableHeadCellClass}>નોંધ</TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    આવક
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    જાવક
                  </TableHead>
                  <TableHead className={tableHeadCellClass}>તારીખ</TableHead>
                  {actionCount > 0 && (
                    <TableHead className={cx(tableHeadCellClass, "text-right")}>
                      એકશન્સ બટન
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>

              {isLoading ? (
                <TableLoadingRow colSpan={tableColSpan} />
              ) : accountTypes.length === 0 ? (
                <TableEmptyRow
                  colSpan={tableColSpan}
                  label="કોઈ પાર્ટી મળી નથી"
                />
              ) : (
                <TableBody>
                  {accountTypes.map((accountType, index) => (
                    <TableRow key={accountType.id}>
                      <TableCell className="font-medium">
                        {(page - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2">
                          <LuBuilding2 className="h-4 w-4 flex-shrink-0 text-gray-400" />
                          <div>
                            <div className="font-medium">
                              {accountType.name || "-"}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {accountType.id}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="font-medium">
                          {accountType.phone || "-"}
                        </div>
                      </TableCell>

                      <TableCell>{accountType.description || "-"}</TableCell>

                      <TableCell className={creditCellClass}>
                        {formatAmount(accountType.credit_amount)}
                      </TableCell>

                      <TableCell className={debitCellClass}>
                        {formatAmount(accountType.debit_amount)}
                      </TableCell>

                      <TableCell className="text-sm text-gray-500">
                        {formatDate(accountType.created_at)}
                      </TableCell>

                      {actionCount > 0 && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {isActionLoading === accountType.id ? (
                              <div className={actionSpinnerClass}></div>
                            ) : (
                              <>
                                {canUpdate && (
                                  <Button
                                    variant="ghost"
                                    size="iconSm"
                                    title="સંપાદિત કરો"
                                    onClick={() =>
                                      handleEditAccountType(accountType)
                                    }
                                  >
                                    <LuPencil className="h-4 w-4" />
                                  </Button>
                                )}

                                {canDelete && (
                                  <Button
                                    variant="ghost"
                                    size="iconSm"
                                    title="કાઢી નાખો"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() =>
                                      handleDeleteRow(accountType.id)
                                    }
                                  >
                                    <LuTrash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:hidden">
        {isLoading ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : accountTypes.length === 0 ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <EmptyState label="કોઈ પાર્ટી મળી નથી" />
          </div>
        ) : (
          accountTypes.map((accountType, index) => (
            <div key={accountType.id} className={mobileCardClass}>
              <div className="flex items-start justify-between gap-3 border-b p-4">
                <div className="flex min-w-0 items-center gap-2">
                  <LuBuilding2 className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  <div className="min-w-0">
                    <div className="font-medium break-words">
                      {accountType.name || "-"}
                    </div>
                    <div className="text-sm text-gray-500">
                      ID: {accountType.id}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3 p-4 text-sm">
                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ક્રમ</span>
                  <span className="font-medium">
                    {(page - 1) * pageSize + index + 1}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ફોન નંબર</span>
                  <div className="min-w-0 text-right break-words">
                    <div className="font-medium">
                      {accountType.phone || "-"}
                    </div>
                  </div>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>નોંધ</span>
                  <span className="min-w-0 text-right break-words">
                    {accountType.description || "-"}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>આવક</span>
                  <span className={creditCellClass}>
                    {formatAmount(accountType.credit_amount)}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>જાવક</span>
                  <span className={debitCellClass}>
                    {formatAmount(accountType.debit_amount)}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>તારીખ</span>
                  <span className="text-right text-gray-500">
                    {formatDate(accountType.created_at)}
                  </span>
                </div>
              </div>

              {actionCount > 0 && (
                <div
                  className={cx(
                    "grid gap-2 border-t p-4",
                    actionCount === 1 ? "grid-cols-1" : "grid-cols-2"
                  )}
                >
                  {isActionLoading === accountType.id ? (
                    <div className="col-span-full flex h-10 items-center justify-center">
                      <div className={actionSpinnerClass}></div>
                    </div>
                  ) : (
                    <>
                      {canUpdate && (
                        <Button
                          variant="outline"
                          title="સંપાદિત કરો"
                          onClick={() => handleEditAccountType(accountType)}
                        >
                          <LuPencil className="h-4 w-4" />
                        </Button>
                      )}

                      {canDelete && (
                        <Button
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          title="કાઢી નાખો"
                          onClick={() => handleDeleteRow(accountType.id)}
                        >
                          <LuTrash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {!isLoading && accountTypes.length > 0 && (
        <Pagination
          itemsPerPage={pageSize}
          totalItems={totalAccountTypes}
          paginate={paginate}
          currentPage={page}
          onPageSizeChange={handlePageSizeChange}
          itemLabel="પાર્ટી"
        />
      )}

      <AccountTypeFormModal handleSaveAccountType={handleSaveAccountType} />
    </div>
  );
};
export default AccountTypeList;
